import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icons } from "../Icons";
import { usePrefs } from "@/lib/prefs";
import { get } from "@/lib/api";
import { searchPages, type PalettePage } from "@/nav/palette";

interface SearchHit {
  kind: "asset" | "location" | "site" | "client" | "work_order" | "nc" | "ticket" | "vehicle" | "user" | "page";
  id: number | string;
  label: string;
  sub: string | null;
  path: string;
}

interface JumpItem { ic: keyof typeof Icons; t: string; k?: string; s?: string }
interface JumpSection { sec: string; items: JumpItem[] }

const KIND_ICONS: Record<SearchHit["kind"], keyof typeof Icons> = {
  asset:      "Drop",
  location:   "Pin",
  site:       "Building",
  client:     "User",
  work_order: "File",
  nc:         "AlertTri",
  ticket:     "Ticket",
  vehicle:    "Truck",
  user:       "User",
  page:       "ArrowR",
};

/**
 * ⌘K command palette. Phase 1.2 — types 2+ chars, hits /v1/search,
 * navigates on Enter / click. Empty query shows the legacy "Jump to"
 * shortcuts so the palette is still useful as a nav helper.
 */
export default function CommandPalette({ open, onClose, pages = [], jumps = [] }: {
  open: boolean; onClose: () => void; pages?: PalettePage[]; jumps?: PalettePage[];
}) {
  const [q, setQ] = useState("");
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const nav = useNavigate();
  const { toggleTheme } = usePrefs();
  const reqId = useRef(0);

  // Debounced search. 200ms after last keystroke; ignored if a newer
  // request supersedes us (so an in-flight slow response doesn't
  // overwrite the latest one).
  useEffect(() => {
    if (!open) return;
    const trimmed = q.trim();
    if (trimmed.length < 2) { setHits([]); setLoading(false); return; }
    const id = ++reqId.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const res = await get<{ results: SearchHit[] }>(`/v1/search?q=${encodeURIComponent(trimmed)}&limit=20`);
        if (reqId.current !== id) return;
        setHits(res.results || []);
        setActive(0);
      } catch {
        if (reqId.current === id) setHits([]);
      } finally {
        if (reqId.current === id) setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  // Reset on open/close.
  useEffect(() => { if (!open) { setQ(""); setHits([]); setActive(0); } }, [open]);

  if (!open) return null;

  const showShortcuts = q.trim().length < 2;
  const merged: SearchHit[] = [
    ...searchPages(pages, q).map((p) => ({ kind: "page" as const, id: p.path, label: p.label, sub: p.context || null, path: p.path })),
    ...hits,
  ];
  const jumpSections: JumpSection[] = [
    { sec: "Jump to", items: jumps.map((j) => ({ ic: j.icon, t: j.label, k: j.path })) },
    { sec: "Actions", items: [
      { ic: "Moon", t: "Toggle dark mode", s: "⌘ \\" },
    ]},
  ];

  const go = (path: string) => {
    if (/^https?:\/\//.test(path)) window.location.href = path;
    else nav(path);
  };

  const onJump = (it: JumpItem) => {
    if (it.k) { go(it.k); onClose(); return; }
    if (it.t === "Toggle dark mode") { toggleTheme(); onClose(); return; }
  };

  const onPick = (h: SearchHit) => {
    go(h.path);
    onClose();
  };

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (showShortcuts) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(merged.length - 1, i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(0, i - 1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      const h = merged[active];
      if (h) onPick(h);
    }
  };

  return (
    <>
      <div className="scrim" onClick={onClose} />
      <div style={{
        position: "fixed", left: "50%", top: "15%", transform: "translateX(-50%)",
        width: 620, maxWidth: "calc(100vw - 32px)",
        background: "var(--warm-card)", border: "1px solid var(--warm-border)",
        borderRadius: 14, boxShadow: "var(--shadow-xl)", zIndex: 1001,
        animation: "popIn 200ms", overflow: "hidden",
        display: "flex", flexDirection: "column", maxHeight: "70vh",
      }}>
        <div className="row" style={{ padding: "14px 16px", borderBottom: "1px solid var(--warm-border)", gap: 10 }}>
          <Icons.Search size={18} className="muted" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
                 placeholder="Search assets, sites, work orders, tickets, vehicles…"
                 style={{ border: 0, outline: 0, flex: 1, fontSize: 15,
                          background: "transparent", color: "var(--warm-text)",
                          fontFamily: "var(--font-display)" }} />
          <span style={{
            fontSize: 11, fontFamily: "var(--font-mono)",
            padding: "2px 7px", border: "1px solid var(--warm-border)",
            borderRadius: 5, color: "var(--warm-text-muted)",
          }}>esc</span>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
          {showShortcuts ? (
            jumpSections.map((g) => (
              <div key={g.sec}>
                <div className="kicker" style={{ padding: "10px 16px 4px", fontSize: 10 }}>{g.sec}</div>
                {g.items.map((it, i) => {
                  const I = Icons[it.ic];
                  return (
                    <div key={i} className="row" style={{ padding: "10px 16px", gap: 12, cursor: "pointer" }}
                         onMouseEnter={(e) => (e.currentTarget.style.background = "var(--warm-canvas)")}
                         onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                         onClick={() => onJump(it)}>
                      <I size={16} className="muted" />
                      <span style={{ flex: 1, fontSize: 13.5 }}>{it.t}</span>
                      {it.s && <span style={{ fontSize: 11, fontFamily: "var(--font-mono)", color: "var(--warm-text-muted)" }}>{it.s}</span>}
                    </div>
                  );
                })}
              </div>
            ))
          ) : loading && merged.length === 0 ? (
            <div className="muted" style={{ padding: "16px", fontSize: 13 }}>Searching…</div>
          ) : merged.length === 0 ? (
            <div className="muted" style={{ padding: "16px", fontSize: 13 }}>
              No results for “{q.trim()}”.
            </div>
          ) : (
            merged.map((h, i) => {
              const I = Icons[KIND_ICONS[h.kind]] ?? Icons.Search;
              const isActive = i === active;
              return (
                <div
                  key={`${h.kind}-${h.id}`}
                  className="row"
                  style={{
                    padding: "10px 16px", gap: 12,
                    cursor: "pointer",
                    background: isActive ? "var(--warm-canvas)" : "transparent",
                  }}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => onPick(h)}
                >
                  <I size={16} className="muted" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13.5 }}>{h.label}</div>
                    {h.sub && <div className="muted" style={{ fontSize: 11.5 }}>{h.sub}</div>}
                  </div>
                  <span className="kicker" style={{ fontSize: 10 }}>{KIND_LABELS[h.kind]}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </>
  );
}

const KIND_LABELS: Record<SearchHit["kind"], string> = {
  asset:      "Asset",
  location:   "Location",
  site:       "Site",
  client:     "Client",
  work_order: "Work order",
  nc:         "Non-conformity",
  ticket:     "Ticket",
  vehicle:    "Vehicle",
  user:       "User",
  page:       "Page",
};
